// Notifications (contracts/admin-api.md "Notifications") and announcements (contracts/messaging.md §1): write a
// message in English, translate it into the app's languages, add an image, a long detail and a button, and
// send it as a push and/or to the in-app inbox. Admins send; every role reads the history. Service messages go
// to every player with notifications on; News & events (promotional) pushes only to players who turned it on in
// the app, as App Review Guideline 4.5.4 requires.

import { useState } from "react";
import { Link } from "react-router";
import {
  AUDIENCE_LABEL,
  type Broadcast,
  type BroadcastAudience,
  type BroadcastDraft,
  type BroadcastKind,
  BROADCAST_LINKS,
  KIND_LABEL,
  LINK_LABEL,
  useBroadcastPreview,
  useBroadcasts,
  useSendBroadcast,
} from "../api/broadcasts";
import { type ContentField, type ImageRef, LOCALE_LABEL, type Locale, NEW_IN_110, RTL_LOCALES } from "../api/messaging";
import { useConfig } from "../api/queries";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Segmented, Select } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { fmtInt, shortId } from "../lib/format";
import { MESSAGE_REQUIRED, resolveEntry, toContent } from "../lib/messaging";
import {
  ActionFields,
  actionFields,
  actionProblem,
  type ActionValue,
  ContentEditor,
  type ContentState,
  emptyContentState,
  type FieldSpec,
  ImageField,
  MESSAGE_FIELD_SPECS,
  noAction,
  type Picked,
  PlayerPicker,
  SwitchRow,
} from "./messaging/components";
import m from "./messaging/messaging.module.css";
import n from "./notifications.module.css";
import styles from "./pages.module.css";

/** Announcements carry every field; the detail is the long text of the app's detail screen. */
const FIELDS: readonly ContentField[] = ["title", "body", "detail", "cta"];
const FIELD_SPECS: readonly FieldSpec<ContentField>[] = [
  { ...MESSAGE_FIELD_SPECS.title, placeholder: "e.g. The IQ Daily Tournament starts Monday" },
  { ...MESSAGE_FIELD_SPECS.body, placeholder: "e.g. The top 10 players share $500 in gift cards. Free to enter.", hint: "the push text and the inbox preview" },
  { ...MESSAGE_FIELD_SPECS.detail, placeholder: "Optional: the full story on the detail screen. A blank line starts a new paragraph.", hint: "optional, shown on the detail screen" },
  { ...MESSAGE_FIELD_SPECS.cta, placeholder: "e.g. Enter now" },
];
/** Recommended announcement image (contracts/messaging.md "Images"). */
const IMAGE = { width: 1200, height: 675 };
const BUTTON_ACTIONS = ["none", "link", "url", "news_optin"] as const;

export function NotificationsPage() {
  const canSend = useCan("notifications.send");
  const { admin } = useAuth();
  return (
    <>
      <PageHeader
        title="Notifications"
        meta={
          canSend ? (
            <span>Announcements: a push, an inbox message or both, in every app language. Every send is written to the audit log.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · sending needs the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      <div className={n.stack}>
        {canSend ? <Composer /> : null}
        <DeliveryRules />
        <History />
      </div>
    </>
  );
}

// ---------------------------------------------------------------- composer

const KIND_HINT: Record<BroadcastKind, string> = {
  service: "Something every player should know: an outage, a fix, an important change. Not for promotions.",
  news: "Tournaments, new features, offers. Only players who turned on News & events in the app get the push (Apple allows promotional pushes only after that opt-in); the inbox shows it to the whole audience.",
};

function Composer() {
  const [kind, setKind] = useState<BroadcastKind>("service");
  const [audience, setAudience] = useState<BroadcastAudience>("all");
  const [player, setPlayer] = useState<Picked | null>(null);
  const [content, setContent] = useState<ContentState>(emptyContentState);
  const [locale, setLocale] = useState<Locale>("en");
  const [image, setImage] = useState<ImageRef | null>(null);
  const [action, setAction] = useState<ActionValue>(noAction);
  const [push, setPush] = useState(true);
  const [inbox, setInbox] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sent, setSent] = useState<Broadcast | null>(null);
  const send = useSendBroadcast();
  const preview = useBroadcastPreview(kind, audience, player?.userId, true);
  const recipients = preview.data?.recipients ?? null;
  const inboxReach = preview.data?.inbox ?? null;

  const { content: payload, incomplete } = toContent(content.draft, FIELDS);
  const en = content.draft.en;
  const shown = resolveEntry(content.draft, locale);
  const hasDetail = Boolean(en.detail.trim() || image || action.action !== "none");

  // Why it can't go out yet, the first reason shown under the buttons.
  const contentProblem = !en.title.trim() || !en.body.trim()
    ? "Write the English title and message."
    : action.action !== "none" && !en.cta.trim()
      ? "The button needs an English label."
      : (actionProblem(action) ?? (incomplete.length ? `Finish or clear ${incomplete.map((l) => LOCALE_LABEL[l]).join(", ")}.` : null));
  const channelProblem = !push && !inbox ? "Turn on the push, the inbox or both." : null;
  const audienceProblem =
    audience === "not_played_today" && !push
      ? "“Haven't played today” is for pushes: turn the push on."
      : audience === "player" && !player
        ? "Pick a player."
        : push && !inbox && recipients === 0
          ? "Nobody to send to: no player in this audience has a push device right now."
          : null;
  const problem = contentProblem ?? channelProblem ?? audienceProblem;
  const ready = problem === null && recipients !== null;
  const testReady = contentProblem === null && channelProblem === null;

  const draft = (over: Partial<BroadcastDraft> = {}): BroadcastDraft => {
    const a = actionFields(action);
    return {
      kind,
      audience,
      ...(audience === "player" && player ? { userId: player.userId } : {}),
      content: payload,
      link: action.link,
      action: a.action,
      ...(a.url ? { url: a.url } : {}),
      ...(image ? { imageId: image.id } : {}),
      push,
      inbox,
      ...over,
    };
  };

  const submit = (d: BroadcastDraft, after: () => void) =>
    send.mutate(d, {
      onSuccess: (b) => {
        setSent(b);
        after();
      },
    });

  const reach = (
    <div className={n.reach} data-testid="recipients">
      {audience === "player" && !player ? (
        <span className={styles.faint}>Pick a player</span>
      ) : preview.isError ? (
        <InlineError error={preview.error} />
      ) : recipients === null || inboxReach === null ? (
        <span className={styles.faint}>Counting…</span>
      ) : (
        <>
          {push ? (
            <span>
              Push to <strong className="mono">{fmtInt(recipients)}</strong>
            </span>
          ) : null}
          {push && inbox ? <span className={n.dot}>·</span> : null}
          {inbox ? (
            <span>
              Inbox for <strong className="mono">{fmtInt(inboxReach)}</strong>
            </span>
          ) : null}
          {!push && !inbox ? <span className={styles.faint}>No channel on</span> : null}
        </>
      )}
    </div>
  );

  return (
    <Card title="New announcement" actions={<Chip tone="muted">Quiet hours respected · pushes expire after 24 h</Chip>}>
      <div className={n.composer}>
        <div className={n.form}>
          <Field label="Type" hint={KIND_HINT[kind]}>
            <Segmented
              label="Type"
              value={kind}
              onChange={setKind}
              options={[
                { value: "service", label: KIND_LABEL.service },
                { value: "news", label: KIND_LABEL.news },
              ]}
            />
          </Field>
          <Field label="Audience" hint={audience === "player" ? "One player, e.g. yourself to test. It ignores the News & events opt-in." : audience === "not_played_today" ? "Checked again in each player's local date when the push goes out." : undefined}>
            <Segmented
              label="Audience"
              value={audience}
              onChange={(a) => {
                setAudience(a);
                if (a !== "player") setPlayer(null);
              }}
              options={(["all", "not_played_today", "player"] as const).map((a) => ({ value: a, label: AUDIENCE_LABEL[a] }))}
            />
          </Field>
          {audience === "player" ? <PlayerPicker picked={player} onPick={setPlayer} /> : null}

          <div className={n.channels} role="group" aria-label="Channels">
            <SwitchRow
              title="Push"
              hint={kind === "news" ? "To players with a push device who turned on News & events." : "To every player with a push device."}
              checked={push}
              onChange={setPush}
              label="Push"
              testId="channel-push"
            />
            <SwitchRow title="Inbox" hint="Listed in the app's inbox for 60 days, with or without a device." checked={inbox} onChange={setInbox} label="Inbox" testId="channel-inbox" />
          </div>

          <ContentEditor
            value={content}
            onChange={setContent}
            fields={FIELD_SPECS}
            required={MESSAGE_REQUIRED}
            locale={locale}
            onLocale={setLocale}
            needs={{ cta: action.action !== "none" ? "required for the button" : null }}
          />

          <ImageField image={image} onChange={setImage} recommended={IMAGE} hint="1200×675 (16:9) recommended" />

          <ActionFields value={action} onChange={setAction} actions={BUTTON_ACTIONS} />

          {push && action.action !== "link" ? (
            <Field
              label="Push tap opens"
              hint={
                hasDetail
                  ? "App 1.1.0 opens the detail screen (there is a detail, an image or a button); older versions open this place."
                  : `No detail screen: a tap opens this place${NEW_IN_110.has(action.link) ? " (older app versions: Home)" : ""}.`
              }
            >
              <Select value={action.link} onChange={(e) => setAction({ ...action, link: e.target.value as ActionValue["link"] })} className={n.linkSelect} data-testid="push-link">
                {BROADCAST_LINKS.map((l) => (
                  <option key={l} value={l}>
                    {LINK_LABEL[l]}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
        </div>

        <aside className={n.side}>
          <div className={m.previewHead}>
            <span className="monoLabel">Preview · {LOCALE_LABEL[locale]}</span>
            {shown.fallback.length ? <span className={m.fallbackNote}>English for: {shown.fallback.join(", ")}</span> : null}
          </div>
          {push ? <PushPreview title={shown.title} body={shown.body} /> : null}
          {inbox || hasDetail ? (
            <DetailPreview entry={shown} image={image} action={action} rtl={RTL_LOCALES.has(locale)} />
          ) : (
            <p className={n.tapNote}>A tap opens {LINK_LABEL[action.link]}: no detail screen.</p>
          )}
          {reach}
          {kind === "news" && audience !== "player" && push && recipients === 0 ? (
            <p className={n.warn}>
              {inbox
                ? "Nobody has turned on News & events yet, so no push goes out: it lands in the inbox only."
                : "Nobody has turned on News & events yet. Turn the inbox on, or send a service message."}
            </p>
          ) : null}
          <div className={n.actions}>
            <Button variant="secondary" disabled={!testReady} onClick={() => setTesting(true)}>
              Send test…
            </Button>
            <Button variant="primary" disabled={!ready} onClick={() => setConfirming(true)} data-testid="send">
              Send…
            </Button>
          </div>
          {problem ? (
            <p className={styles.faint} data-testid="send-problem">
              {problem}
            </p>
          ) : null}
          {sent ? (
            <p className={styles.notice} data-testid="sent">
              Sent “{sent.title}”{sent.push ? `: ${fmtInt(sent.counts.sent)} pushed` : ""}
              {sent.counts.waiting ? `, ${fmtInt(sent.counts.waiting)} waiting for their quiet hours to end` : ""}
              {sent.inbox ? (sent.push ? " · in the inbox" : ": in the inbox") : ""}.
            </p>
          ) : null}
        </aside>
      </div>

      <ConfirmDialog
        open={confirming}
        title="Send this announcement?"
        confirmLabel="Send now"
        busy={send.isPending}
        error={send.error}
        onCancel={() => {
          setConfirming(false);
          send.reset();
        }}
        onConfirm={() =>
          submit(draft(), () => {
            setConfirming(false);
            setContent(emptyContentState());
            setImage(null);
            setAction(noAction());
            setLocale("en");
          })
        }
      >
        <p className={styles.prose} data-testid="confirm-summary">
          {KIND_LABEL[kind]} · {AUDIENCE_LABEL[audience]}
          {player ? ` (${player.handle ? `@${player.handle}` : shortId(player.userId)})` : ""} · {[push && `push to ${fmtInt(recipients ?? 0)}`, inbox && `inbox for ${fmtInt(inboxReach ?? 0)}`].filter(Boolean).join(", ")}
        </p>
        <PushPreview title={en.title} body={en.body} />
        <p className={styles.footNote}>
          {Object.keys(payload).length} {Object.keys(payload).length === 1 ? "language" : "languages"}
          {action.action !== "none" ? ` · button opens ${action.action === "link" ? LINK_LABEL[action.link] : action.action === "url" ? action.url.trim() : "News & events"}` : ""}
          {push && action.action !== "link" ? ` · tap opens ${hasDetail ? "the detail screen" : LINK_LABEL[action.link]}` : ""}. It can't be taken back. Players inside their quiet hours get the push when the hours end.
        </p>
      </ConfirmDialog>

      <TestDialog
        open={testing}
        busy={send.isPending}
        error={send.error}
        onCancel={() => {
          setTesting(false);
          send.reset();
        }}
        onSend={(p) => submit(draft({ audience: "player", userId: p.userId }), () => setTesting(false))}
      />
    </Card>
  );
}

function PushPreview({ title, body }: { title: string; body: string }) {
  return (
    <div className={n.push} aria-label="Notification preview">
      <span className={n.icon} aria-hidden>
        IQ
      </span>
      <div className={n.pushText}>
        <div className={n.pushHead}>
          <strong>{title.trim() || "Title"}</strong>
          <span>now</span>
        </div>
        <p>{body.trim() || "Your message"}</p>
      </div>
    </div>
  );
}

/** The app's announcement screen (contracts/messaging.md §3): the image, the title, the detail (else the body), the button, the date. */
function DetailPreview({ entry, image, action, rtl }: { entry: { title: string; body: string; detail: string; cta: string }; image: ImageRef | null; action: ActionValue; rtl: boolean }) {
  const paragraphs = (entry.detail || entry.body || "Your message").split(/\n\s*\n/).filter((p) => p.trim());
  return (
    <div className={m.phone} aria-label="Detail screen preview" data-testid="detail-preview" dir={rtl ? "rtl" : undefined}>
      <div className={m.phoneBar}>
        <span>‹ Inbox</span>
        <span>Today</span>
      </div>
      {image ? <img src={image.url} alt="" className={n.detailImage} /> : null}
      <h3 className={n.detailTitle}>{entry.title || "Title"}</h3>
      <div className={n.detailText}>
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      {action.action !== "none" ? <span className={n.detailButton}>{entry.cta || "Button"}</span> : null}
    </div>
  );
}

function TestDialog({ open, busy, error, onCancel, onSend }: { open: boolean; busy: boolean; error: unknown; onCancel: () => void; onSend: (p: Picked) => void }) {
  const [picked, setPicked] = useState<Picked | null>(null);
  return (
    <Dialog
      open={open}
      title="Send a test"
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!picked} onClick={() => picked && onSend(picked)}>
            Send test
          </Button>
        </>
      }
    >
      <p className={styles.prose}>Sends this announcement to one player only, e.g. your own IQ Daily account, to check how it looks: the push and, with the inbox on, the inbox item.</p>
      <PlayerPicker picked={picked} onPick={setPicked} />
      <InlineError error={error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------- how delivery works

function DeliveryRules() {
  const config = useConfig();
  const quiet = config.data?.values["notifications.quietHours"] as { start: string; end: string } | undefined;
  const cap = config.data?.values["notifications.dailyCap"] as number | undefined;
  return (
    <details className={n.rules} data-testid="delivery-rules">
      <summary>How delivery works</summary>
      <ul>
        <li>
          <strong>Service messages</strong> reach every player with notifications allowed. <strong>News & events</strong> pushes reach only players who turned it on (Profile →
          Notifications); in the app (the inbox, banners) it reaches everyone in the audience.
        </li>
        <li>
          <strong>Quiet hours</strong> (<code>notifications.quietHours</code>, each player's local time{quiet ? `: now ${quiet.start}–${quiet.end}` : ""}): pushes wait until the hours end.
          A push not delivered within 24 h is dropped. Inbox items appear at once.
        </li>
        <li>
          <strong>Daily cap</strong> (<code>notifications.dailyCap</code>
          {cap !== undefined ? `: now ${cap}` : ""}): announcements don't count against it, but they count for the automatic pushes that day. Support replies ignore the cap and quiet
          hours.
        </li>
        <li>Each player gets an announcement at most once. Dead device tokens are removed.</li>
        <li>
          <strong>Inbox</strong>: items show for 60 days. “Everyone” includes accounts created up to 14 days after the send; “Haven't played today” lists it only for the players it was
          pushed to.
        </li>
        <li>
          <strong>Languages</strong>: each player sees their own language when there is a translation, else English, field by field. A push uses the device's language.
        </li>
        <li>
          <strong>The tap</strong>: app 1.1.0 opens the detail screen when there is a detail, an image or a button; otherwise, and on 1.0.0, it opens the “Push tap opens” place.
        </li>
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------- history

const STATUS_TONE = { sending: "warning", done: "success" } as const;

function buttonText(b: Broadcast): string {
  if (b.action === "link") return LINK_LABEL[b.link];
  return b.action === "url" ? "web page" : b.action === "news_optin" ? "News & events" : "announcement";
}

function History() {
  const list = useBroadcasts();
  return (
    <Card title="Sent" padded={false}>
      {list.isPending ? (
        <Loading rows={4} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title="Nothing sent yet">Announcements you send show up here with how many were delivered, read and tapped.</EmptyState>
      ) : (
        <div className={tableStyles.wrap}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>Sent</th>
                <th>Audience</th>
                <th>Message</th>
                <th>Channels</th>
                <th>Delivery</th>
                <th data-align="right">Reads</th>
                <th data-align="right">Taps</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((b) => (
                <tr key={b.id} data-testid="broadcast-row">
                  <td>
                    <span className={tableStyles.mono}>{formatDateTime(b.createdAt)}</span>
                    <span className={n.by}>{b.createdBy ?? "—"}</span>
                  </td>
                  <td>
                    {b.target ? (
                      <Link to={`/players/${b.target.userId}`} className={tableStyles.id}>
                        {b.target.handle ? `@${b.target.handle}` : shortId(b.target.userId)}
                      </Link>
                    ) : (
                      <span className={n.audience}>{AUDIENCE_LABEL[b.audience]}</span>
                    )}
                  </td>
                  <td>
                    <div className={n.message}>
                      {b.image ? <img src={b.image.url} alt="" className={n.rowThumb} data-testid="row-thumb" /> : null}
                      <div>
                        <strong>{b.title}</strong>
                        <span>{b.body}</span>
                        <span className={n.langs}>
                          <Chip tone={b.kind === "news" ? "violet" : "info"}>{KIND_LABEL[b.kind]}</Chip> {Object.keys(b.content).length}{" "}
                          {Object.keys(b.content).length === 1 ? "language" : "languages"}
                          {b.action !== "none" ? ` · button: ${buttonText(b)}` : ""}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={n.chips}>
                      {b.push ? <Chip tone="info">push</Chip> : null}
                      {b.inbox ? <Chip tone="teams">inbox</Chip> : null}
                    </div>
                  </td>
                  <td>
                    <div className={n.delivery}>
                      <div className={n.deliveryHead}>
                        <Chip tone={STATUS_TONE[b.status]}>{b.status === "sending" ? "sending" : "done"}</Chip>
                        <span className="mono">{b.push ? `${fmtInt(b.recipients)} pushed` : "inbox only"}</span>
                      </div>
                      {b.push ? <span className={n.summary}>{deliverySummary(b)}</span> : null}
                    </div>
                  </td>
                  <td data-align="right" className="mono" data-testid="reads">
                    {fmtInt(b.reads)}
                  </td>
                  <td data-align="right" className="mono" data-testid="taps">
                    {fmtInt(b.taps)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function deliverySummary(b: Broadcast): string {
  const c = b.counts;
  const parts = [`${fmtInt(c.sent)} sent`];
  if (c.waiting) parts.push(`${fmtInt(c.waiting)} waiting`);
  if (c.failed) parts.push(`${fmtInt(c.failed)} failed`);
  if (c.skipped) parts.push(`${fmtInt(c.skipped)} skipped`);
  if (c.expired) parts.push(`${fmtInt(c.expired)} expired`);
  if (c.dryRun) parts.push(`${fmtInt(c.dryRun)} not sent (push not configured)`);
  return parts.join(" · ");
}
